import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { ExpandIcon } from "../../atoms";
import CollapseIcon from "../../pages/company-page/collapse-icon";
import ZoomIn from "../../atoms/ZoomIn";
import ZoomOut from "../../atoms/ZoomOut";
import { useDeviceType } from "../../customHooks/useDeviceType";

import "./graph.m.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels);

const RankingTrendGraph = ({
  graphData = [],
  title,
  reversed = true,
  label = true,
  dynamicOptions = [],
  graphExpand = false,
  setGraphExpand,
  extraOptions = false,
  domain = ["dataMin", "dataMax"],
  threeLineOptions = false,
  dot = true,
}) => {
  const chartRef = useRef(null);
  const [zoomPluginLoaded, setZoomPluginLoaded] = useState(false);

  useEffect(() => {
    import("chartjs-plugin-zoom").then((module) => {
      const zoomPlugin = module.default;
      ChartJS.register(zoomPlugin);
      setZoomPluginLoaded(true);
    });
  }, []);

  const filteredData = graphData.filter((obj) => {
    const { value, value1, value2, value3, value4 } = obj;
    return [value, value1, value2, value3, value4].some((val) => val !== undefined);
  });
  const isMobile = useDeviceType() !== "desktop";

  const labels = filteredData.map((item) => item.year);

  useEffect(() => {
    if (graphExpand) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [graphExpand]);

  const datasets = ["value", "value1", "value2", "value3", "value4"]
    .map((key) => {
      const hasData = filteredData.some((item) => item[key] !== undefined);
      if (!hasData) return null;
      return {
        label: key,
        data: filteredData.map((item) => {
          return (typeof item[key] === 'string' && item[key].includes('%')) ?  item[key].split('%')[0] : item[key];
        }),
        borderColor: graphData[0]?.[`${key}color`] || "rgba(63, 186, 124, 0.56)",
        backgroundColor: "#C6554F",
        pointRadius: dot ? 3 : 0,
        pointBackgroundColor: "#C6554F",
        borderWidth: 2,
        tension: 0.4,
        fill: false,
      };
    })
    .filter(Boolean);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
      datalabels: {
        display: label,
        align: "top",
        offset: 10,
        color: "#000000bf",
        font: {
          size: 12,
        },
        formatter: (value) => `#${value}`,
        textAlign: "center",
      },
      ...(zoomPluginLoaded &&
        graphExpand && {
          zoom: {
            pan: {
              enabled: true,
              mode: "xy",
              threshold: 1,
              speed: 1,
              smooth: true,
            },
            pinch: {
              enabled: true,
            },
          },
        }),
    },
    scales: {
      x: {
        grid: {
          drawVerticalLines: true,
          drawHorizontalLines: false,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          display: !isMobile,
          color: "rgba(0, 0, 0, 0.75)",
          font: {
            size: 12,
            weight: "700",
            lineHeight: 1.0,
            family: "Roboto Slab",
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        reverse: reversed,
        min: domain[0] === "dataMin" ? undefined : domain[0],
        max: domain[1] === "dataMax" ? undefined : domain[1],
        grid: {
          drawBorder: false,
          display: false,
        },
        ticks: {
          padding: 10,
          display: !isMobile && graphExpand,
          color: "rgba(0, 0, 0, 0.75)",
          font: {
            size: 12,
            weight: "700",
            lineHeight: 1.0,
            family: "Roboto Slab",
          },
        },
        border: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        top: 30,
        right: 30,
        left: 20,
        bottom: 10,
      },
    },
  };

  const data = {
    labels,
    datasets,
  };

  const handleZoomIn = () => {
    if (chartRef.current) {
      chartRef.current.zoom(1.1);
    }
  };
  const handleZoomOut = () => {
    if (chartRef.current) {
      chartRef.current.zoom(0.9);
    }
  };

  return (
    <React.Fragment>
      {graphExpand && <div styleName="blur-bg" />}
      <div styleName={`graph-container ${graphExpand ? "expanded-container" : ""}`}>
        <div styleName="expand" onClick={() => setGraphExpand(!graphExpand)}>
          {graphExpand ? <CollapseIcon /> : <ExpandIcon />}
        </div>
        {title ? <div styleName="graph-title">{title}</div> : <div styleName="no-title-collapsed" />}
        <div styleName={`graph-wrapper ${graphExpand && threeLineOptions ? "threeLineOptions-container" : ""}`}>
          <Line ref={chartRef} options={options} data={data} plugins={[ChartDataLabels]} />
        </div>
        {graphExpand && dynamicOptions.length > 0 && (
          <div styleName="financial-data-options">
            {dynamicOptions.map((obj, index) => {
              return (
                <React.Fragment key={index}>
                  <input
                    type="checkbox"
                    styleName={`checkbox ${obj.present ? "" : "grey-out"} ${
                      extraOptions && index === 1
                        ? "option-name-mobile"
                        : extraOptions && index === 5
                        ? "option-name-desktop"
                        : ""
                    }`}
                    style={{ "--color": obj.color }}
                    checked={!obj.present ? false : obj.value}
                    onChange={() => obj.setvalue(!obj.value)}
                  />
                  <span
                    styleName={`option-name ${obj.present ? "" : "grey-out"} ${
                      extraOptions && index === 1
                        ? "option-name-mobile"
                        : extraOptions && index === 5
                        ? "option-name-desktop"
                        : ""
                    }`}
                  >
                    {obj.name}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        )}
        {graphExpand && zoomPluginLoaded && (
          <div styleName="zoom-controls">
            <div onClick={handleZoomIn} styleName="zoom-controls-button">
              <ZoomIn />
            </div>
            <siv onClick={handleZoomOut} styleName="zoom-controls-button">
              <ZoomOut />
            </siv>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

RankingTrendGraph.propTypes = {
  graphData: PropTypes.array,
  title: PropTypes.string,
  reversed: PropTypes.bool,
  label: PropTypes.bool,
  dynamicOptions: PropTypes.array,
  setGraphExpand: PropTypes.func,
  graphExpand: PropTypes.bool,
  domain: PropTypes.array,
  extraOptions: PropTypes.bool,
  dot: PropTypes.bool,
  threeLineOptions: PropTypes.bool,
};

export default RankingTrendGraph;
